<template>
    <div id="responsiveDash" 
         class="c-responsiveDash" 
         ref="responsiveDash" 
         @pointerdown.capture="lockHeight"
         :style="{ 
             display: 'flex', 
             flexGrow: 1, 
             paddingBottom: editing ? '200px' : '0px',
             minHeight: lockedHeight 
         }">
        <div v-if="layout.items.length == 0" style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%; min-height: 60vh; gap: 32px;">
            <QIcon
                name="mdi-chart-box-plus-outline"
                style="font-size: 20vh; color: #BDBDBD;"
            />
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 10px;">
                <div>
                    <b style="font-size: 24px">
                        {{ Translate("Start building your dashboard!") }}
                    </b>
                </div>
                <div v-if="!editing" style="display: flex; align-items: center;">
                    {{ Translate("Select") }} <QIcon name="mdi-pencil" size="sm" class="q-mx-xs" /> {{ Translate("to begin adding cards from the library.") }}
                </div>
            </div>
        </div>
        <GridLayout v-if="localItems.length > 0"
                    v-model:layout="localItems"
                    @layout-updated="syncLayout"
                    :row-height="rowHeight"
                    :col-num="colNum"
                    :responsive="false"
                    :is-draggable="true"
                    :is-resizable="true"
                    style="display:flex; flex-direction:column; flex-grow:1;">
            <template #item="{ item }">
                <QCard class="content" flat bordered style="user-select: text; box-shadow: 0 3px 6px rgba(0,0,0,0.16);">
                    <QToolbar dense class="bg-transparent q-px-md" title="">
                        <QToolbarTitle class="text-h6 text-dark q-pa-none col-auto" v-if="editing && item.state.isEditing">
                            <QInput v-model="item.title" dense outlined hide-bottom-space class="q-pt-xs q-pb-xs" style="min-width: 120px;" />
                        </QToolbarTitle>
                        
                        <QToolbarTitle title="" class="text-h6 text-dark q-pa-none text-left col-auto" v-else>
                            {{ item.title || ('Untitled ' + item.id) }}
                        </QToolbarTitle>
                        
                        <QBtn class="q-ml-sm" flat round dense size="sm" icon="mdi-pencil" v-if="editing && !item.state.isEditing" @click="editTitle($event, item)" />
                        <QBtn class="q-ml-sm" flat round dense size="sm" icon="mdi-check" v-if="editing && item.state.isEditing" @click="saveTitle($event, item)" />
                        <QBtn flat round dense size="sm" icon="mdi-close" v-if="editing && item.state.isEditing" @click="cancelEditTitle($event, item)" />
                        
                        <QSpace />
                        
                        <QBtn flat round dense size="sm" icon="mdi-cog-outline" v-if="editing && configurable && configurable(item.data)" :title="Translate('Configure')" :aria-label="Translate('Configure')" @click="$emit('configure', item.data)" />
                        <QBtn flat round dense size="sm" icon="mdi-trash-can-outline" v-if="editing" @click="removeThis($event, item)" />
                    </QToolbar>
                    <div class="dashitem_content" title="">
                        <slot name="content" :item="item.data">
                            This is the content
                        </slot>
                    </div>
                </QCard>
            </template>
        </GridLayout>
        <QDialog v-model="pickervisible" :maximized="false" position="standard">
            <QCard :style="{ width: dialog_width + 'px', maxWidth: '100vw' }">
                <QBar flat class="q-bar--banner" :style="{ backgroundColor: 'var(--v-navigation-base)', color: 'white', minHeight: '60px' }">
                    <QToolbarTitle class="text-white q-pl-none">
                        {{ Translate("Delete Card") }}
                    </QToolbarTitle>
                    <QSpace />
                    <QBtn flat round dense icon="mdi-close" color="white" @click="pickervisible = false; selectedItem = -1;" />
                </QBar>
                <QCardSection class="q-pt-md">
                    {{ Translate("Are you sure you want to delete?") }}
                </QCardSection>
                <QCardActions align="right" class="q-pr-md q-pb-md">
                    <QBtn color="primary" no-caps class="c-SentenceCaseButton" @click="$emit('remove', selectedItem); pickervisible = false;">
                        {{ Translate("Yes") }}
                    </QBtn>
                </QCardActions>
            </QCard>
        </QDialog>
    </div>
</template>

<script>
    import { QIcon, QCard, QToolbar, QToolbarTitle, QInput, QBtn, QSpace, QDialog, QBar, QCardSection, QCardActions } from 'quasar';
    import methods from "@/Shared/methods";
    import { GridLayout, GridItem } from 'grid-layout-plus';

    export default {
        name: "ResponsiveDash",
        emits: ['remove', 'configure'],
        components: {
            QIcon,
            QCard,
            QToolbar,
            QToolbarTitle,
            QInput,
            QBtn,
            QSpace,
            QDialog,
            QBar,
            QCardSection,
            QCardActions,
            GridLayout,
            GridItem,
        },
        data: function () {
            return {
                old_title: '',
                tb_icon: 'mdi-dots-vertical',
                selectedItem: -1,
                dialog_width: 400,
                picker: null,
                pickervisible: false,
                isEditingTitle: false,

                observer: null,
                containerWidth: 0,
                localItems: [],
                lockedHeight: 'auto', 
                currentMaxHeight: 0,
                isInteracting: false,
            };
        },
        props: {
            layout: null,
            editing: false,
            toolbar_icon: null,
            root: null,
            // (item) => bool, supplied by the dashboard host: true when the card has a prompt to reopen
            configurable: { type: Function, default: null },
        },
        created() {
            if (this.toolbar_icon)
                this.tb_icon = this.toolbar_icon;
        },
        mounted() {
            this.observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                
                this.containerWidth = entry.contentRect.width;
                
                // Ratchet the min-height upwards if the container grows during a drag
                if (this.isInteracting && this.$refs.responsiveDash) {
                    const currentHeight = this.$refs.responsiveDash.offsetHeight;
                    
                    if (currentHeight > this.currentMaxHeight) {
                        this.currentMaxHeight = currentHeight;
                        this.lockedHeight = currentHeight + 'px';
                    }
                }
            });

            if (this.$refs.responsiveDash) {
                this.observer.observe(this.$refs.responsiveDash);
            }

            // Existing event listeners for mouse release
            window.addEventListener('pointerup', this.unlockHeight);
            window.addEventListener('pointercancel', this.unlockHeight);
        },
        beforeUnmount() {
            // Clean up listeners when the component is destroyed
            window.removeEventListener('pointerup', this.unlockHeight);
            window.removeEventListener('pointercancel', this.unlockHeight);
            
            if (this.observer && this.$refs.responsiveDash) {
                this.observer.unobserve(this.$refs.responsiveDash);
            }
        },
        computed: {
            rowHeight: function () {
                // Roughly similar to the Vue2 version
                return 150 * this.containerWidth / 2000 * 12 / this.colNum;
            },
            colNum: function () {
                return this.layout.numberOfCols;
            },
        },
        methods: {
            ...methods,
            removeThis(e, item) {
                this.pickervisible = true;
                this.selectedItem = item;
            },
            findLayoutItem(localItem) {
                // grid-layout-plus uses string `i`; host layout keeps id as number or string
                return this.layout.items.find(i => String(i.id) === String(localItem.i));
            },
            editTitle(e, item) {
                this.old_title = item.title;
                item.state.isEditing = true;
            },
            saveTitle(e, item) {
                // localItems is a copy for the grid; write the title back so saveTab persists it (CCDEV-17726)
                const originalItem = this.findLayoutItem(item);
                if (originalItem)
                    originalItem.title = item.title;
                item.state.isEditing = false;
            },
            cancelEditTitle(e, item) {
                item.title = this.old_title;
                item.state.isEditing = false;
            },
            syncLayout(newLayout) {
                newLayout.forEach(localItem => {
                    const originalItem = this.findLayoutItem(localItem);
                    if (originalItem) {
                        originalItem.x = localItem.x;
                        originalItem.y = localItem.y;
                        originalItem.width = localItem.w;
                        originalItem.height = localItem.h;
                    }
                });
            },
            lockHeight() {
                if (this.editing && this.$refs.responsiveDash) {
                    this.isInteracting = true;
                    this.currentMaxHeight = this.$refs.responsiveDash.offsetHeight;
                    this.lockedHeight = this.currentMaxHeight + 'px';
                }
            },
            
            unlockHeight() {
                this.isInteracting = false;
                this.currentMaxHeight = 0;
                this.lockedHeight = 'auto';
            }
        },
        watch: {
            'layout.items': {
                handler(newVal) {
                    // Preserve in-progress title edits across layout sync (drag/resize rebuilds this list)
                    const prevById = new Map(this.localItems.map(li => [String(li.i), li]));
                    this.localItems = newVal.map(i => {
                        const prev = prevById.get(String(i.id));
                        const editingTitle = prev && prev.state && prev.state.isEditing;
                        return {
                            x: i.x,
                            y: i.y,
                            w: i.width,
                            h: i.height,
                            i: i.id.toString(),
                            static: !this.editing,
                            title: editingTitle ? prev.title : i.title,
                            state: { isEditing: !!editingTitle },
                            data: {
                                id: i.id,
                                ControlURL: i.ControlURL,
                                ControlData: i.ControlData,
                            },
                        };
                    });
                },
                immediate: true,
                deep: true 
            },
            editing(isEditing) {
                this.localItems.forEach(item => {
                    item.static = !isEditing;
                });
            }
        },
    };
</script>

<style scoped>
    .content {
        height: 100%;
        width: 100%;
        text-align: center;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        /* Global .q-card in App.vue paints cards with the page background. */
        background-color: white;
        /*border: 2px solid #42b983;
        background-color: #42b9833d;*/
    }

    .dashitem_content {
        display: flex;
        flex-direction: column;
        width: 100%;
        align-items: stretch;
        flex-grow: 1;
        overflow: hidden;
    }
</style>